import React, { useState } from "react";
import axios from "axios";
import { Phone, MapPin, Clock, Mail, Send } from "lucide-react";
import { businessInfo, payments } from "../mock";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast({ title: "Missing info", description: "Please add your name and phone so Shane can call you back." });
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/contact-requests`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
      });
      toast({ title: "Message sent!", description: `Thanks ${form.name}, Shane will call you at ${form.phone} shortly.` });
      setForm({ name: "", phone: "", message: "" });
    } catch (err) {
      toast({ title: "Couldn't send", description: "Something went wrong. Please call directly for emergencies." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 lg:py-28 bg-black">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16">
        <div>
          <span className="text-yellow-400 font-bold uppercase tracking-[0.3em] text-xs">Get In Touch</span>
          <h2 className="mt-3 text-white font-extrabold text-3xl lg:text-5xl tracking-tight heading-underline">
            Need a Key. Right Now?
          </h2>
          <p className="mt-6 text-zinc-400 text-lg leading-relaxed">
            Calling is fastest — Shane answers 24/7. Or send a quick note and he'll follow up as soon as he's off another job.
          </p>

          <div className="mt-10 space-y-5">
            <InfoRow icon={<Phone />} label="Phone (24/7)" value={businessInfo.phone} href={`tel:${businessInfo.phoneRaw}`} />
            <InfoRow icon={<Mail />} label="Email" value={businessInfo.email} href={`mailto:${businessInfo.email}`} />
            <InfoRow icon={<MapPin />} label="Service Area" value={businessInfo.serviceRegion} />
            <InfoRow icon={<Clock />} label="Hours" value={businessInfo.hours} />
          </div>

          <div className="mt-8 brand-card rounded-xl p-5 border-yellow-500/30">
            <div className="text-yellow-300 text-xs font-bold uppercase tracking-widest mb-2">Accepted Payments</div>
            <div className="flex flex-wrap gap-2">
              {payments.map((p) => (
                <span key={p} className="px-3 py-1.5 rounded-md bg-zinc-900 border border-zinc-700 text-zinc-200 text-sm font-semibold">
                  {p}
                </span>
              ))}
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="brand-card rounded-2xl p-6 lg:p-8 border-red-900/40">
          <h3 className="font-bold text-white text-xl">Request a Call Back</h3>
          <p className="text-zinc-400 text-sm mt-1">Tell Shane where you are and what you need.</p>

          <div className="mt-6 space-y-4">
            <div>
              <label className="text-sm font-semibold text-zinc-200">Your Name</label>
              <Input name="name" value={form.name} onChange={handleChange} placeholder="Jane Doe" className="mt-1.5 h-11 bg-zinc-950 border-zinc-700 text-white placeholder:text-zinc-600" />
            </div>
            <div>
              <label className="text-sm font-semibold text-zinc-200">Phone Number</label>
              <Input name="phone" value={form.phone} onChange={handleChange} placeholder="(606) 555-0123" className="mt-1.5 h-11 bg-zinc-950 border-zinc-700 text-white placeholder:text-zinc-600" />
            </div>
            <div>
              <label className="text-sm font-semibold text-zinc-200">What do you need?</label>
              <Textarea name="message" value={form.message} onChange={handleChange} placeholder="Locked out of my Silverado at the Dollar General on Main St..." className="mt-1.5 min-h-[110px] bg-zinc-950 border-zinc-700 text-white placeholder:text-zinc-600" />
            </div>
          </div>

          <Button type="submit" disabled={submitting} className="mt-6 w-full bg-brand-red text-white font-semibold h-12 disabled:opacity-70">
            <Send className="w-4 h-4 mr-2" />
            {submitting ? "Sending..." : "Send Request"}
          </Button>
          <p className="text-xs text-zinc-500 mt-3 text-center">For emergencies, please call directly.</p>
        </form>
      </div>
    </section>
  );
};

const InfoRow = ({ icon, label, value, href }) => {
  const Content = (
    <>
      <div className="w-11 h-11 shrink-0 rounded-lg bg-red-950/60 border border-red-700/40 text-red-500 flex items-center justify-center">
        {React.cloneElement(icon, { className: "w-5 h-5", strokeWidth: 2.2 })}
      </div>
      <div>
        <div className="text-xs text-zinc-500 font-semibold uppercase tracking-wide">{label}</div>
        <div className="text-white font-semibold mt-0.5">{value}</div>
      </div>
    </>
  );
  return href ? (
    <a href={href} className="flex items-center gap-4 hover:text-red-500 transition-colors">{Content}</a>
  ) : (
    <div className="flex items-center gap-4">{Content}</div>
  );
};

export default Contact;
